import { ThemeTypings, SpaceProps } from '@chakra-ui/styled-system';
import {
  Box,
  theme,
  Link,
  StyleProps,
  useMediaQuery,
  Flex,
} from '@everlywell/ui-kit';
import { Link as GatsbyLink } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React, { MouseEventHandler } from 'react';
import { isFullUrl } from 'utils/helpers';

export type ProductCardVariant = 'default' | 'featured' | 'standard';
export type Size = 'sm' | 'md' | 'lg';

type ColorScheme = {
  background: ThemeTypings['colors'];
  text: ThemeTypings['colors'];
};
type ColorSchemeKey = 'default' | 'lightGreen' | 'green' | 'transparent';

const colorSchemeMap: Record<ColorSchemeKey, ColorScheme> = {
  default: {
    background: 'tints.cream',
    text: 'tints.darkGrey',
  },
  lightGreen: {
    background: 'viridian.wash',
    text: 'tints.darkGrey',
  },
  green: {
    background: 'viridian.base',
    text: 'tints.white',
  },
  transparent: {
    background: 'transparent' as ThemeTypings['colors'],
    text: 'tints.white',
  },
};

export type ProductCardV2Props = {
  /**
   * Image slot for the card. Prefer the Gatsby Image component.
   */
  image: React.ReactNode;
  /**
   * Pricing slot for the card. It should use the CreditValue and PriceV2 components.
   * */
  pricing: React.ReactNode;
  /**
   * Children to be passed inside the component, Generally the product name and a short description
   */
  children: React.ReactNode | React.ReactNode[];
  /**
   * Optional flag to capture the onClick event on the card
   */
  handleOnClick?: MouseEventHandler<HTMLAnchorElement>;
  /**
   * CTA slot for the card. It should use the Button component from the UI Kit.
   */
  cta?: React.ReactNode;
  /**
   * Url to where the card should link to. It usually is the product page.
   * If not provided, the card will not be clickable.
   */
  linkTo?: string;
  /**
   * Optional variant for the card. Defaults to default.
   *
   * Default variant had a cream background and the image is slightly rotated.
   * Featured variant has a viridian wash background and the image is not rotated.
   */
  variant?: ProductCardVariant;
  /**
   * Optional size for the card. Defaults to md.
   */
  size?: Size;
  /**
   * Optional tag to be slot for the card. It should use the Tag component from the UI Kit.
   */
  tag?: React.ReactNode;
  /**
   * Optional flag to render whatever it receives as the price
   */
  pricingOverride?: React.ReactNode;
  /**
   * Color scheme for the card
   */
  colorScheme?: ColorSchemeKey;
  /**
   * Centralize CTA element. For this to work, there should be no pricing element
   */
  centralizeCTA?: boolean;
  /**
   * BackgroundColor for the card
   */
  cardBackgroundColor?: string | null;
};

const getMinCardHeight = (size: Size): [string, string] => {
  switch (size) {
    case 'sm':
      return ['240px', '460px'];
    case 'md':
      return ['160px', '460px'];
    case 'lg':
      return ['380px', '460px'];
  }
};

const getImagePaddingX = (
  variant: ProductCardVariant,
  size: Size,
): SpaceProps['paddingX'] => {
  if (variant === 'standard') {
    return [null, 6];
  }

  if (size === 'md') {
    return [0, 12, null, 16];
  }

  return [10, null, 12, 20];
};

// Define the function to handle the padding logic
const getImagePaddingTop = (
  isStandard: boolean,
  size: Size,
): number | number[] => {
  if (isStandard) {
    return [0, 6];
  }

  if (size === 'md') {
    return [0, 6];
  }

  return [0, 4, 6];
};

/**
 * Product Card V2 component
 * Used to display a product card
 * Uses the Everlywell UI Kit components
 *
 * @param {ProductCardV2Props} props
 *
 */
function ProductCardV2({
  cardBackgroundColor: cbc,
  centralizeCTA,
  children,
  colorScheme = 'default',
  cta,
  handleOnClick,
  image,
  linkTo,
  pricing,
  pricingOverride,
  size = 'md',
  tag,
  variant = 'default',
}: ProductCardV2Props): JSX.Element {
  const { productOverrides } = useFlags();

  const { full } = theme.sizes;

  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.tablet})`);

  const isFeatured = variant === 'featured';
  const isStandard = variant === 'standard';

  const isMd = size === 'md';

  const imagePaddingX = getImagePaddingX(variant, size);
  const imagePaddingTop = getImagePaddingTop(isStandard, size);

  let cardBackgroundColor = cbc;

  if (!cardBackgroundColor) {
    cardBackgroundColor = isFeatured
      ? theme.colors.viridian.wash
      : colorSchemeMap[colorScheme].background;
  }
  const cardFlexDirection: StyleProps['flexDirection'] = isMd
    ? ['row', 'column']
    : ['column', 'column'];
  const cardMinHeight = getMinCardHeight(size);

  const firstBoxMinHeight = isMd ? ['120px', 'unset'] : undefined;

  const childrenTopPadding = isMd ? [2, 'unset'] : undefined;

  const handleCTAClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    // Prevent the default behavior of the click event
    event.preventDefault();
    // Stop the event from propagating to the parent link
    event.stopPropagation();
  };

  let firstMarginBox = 'unset';
  let marginInnerBox1: string | number = 0;

  if (isStandard) {
    marginInnerBox1 = 0;
    const m = `-${theme.space[4]}`;
    firstMarginBox = isMobile ? `${m} 0 ${m} ${m}` : `-${theme.space[6]}`;
  }

  const parsedLinkTo =
    productOverrides?.overrides[`${linkTo}`]?.linkTo || linkTo;

  return (
    <Link
      overflow={isStandard ? 'hidden' : 'unset'}
      data-testid="productCard"
      onClick={handleOnClick}
      // add to prop if the linkTo is internal
      {...(linkTo &&
        (isFullUrl(parsedLinkTo)
          ? { href: parsedLinkTo }
          : { to: parsedLinkTo, as: GatsbyLink }))}
      display="flex"
      height={full}
      backgroundColor={cardBackgroundColor}
      padding="2"
      flexDirection={cardFlexDirection}
      justifyContent="space-between"
      gap={[3, 0]}
      minHeight={cardMinHeight}
      borderRadius={'2xl'} // 16px
      border="1px solid"
      borderColor={cardBackgroundColor}
      _hover={
        colorScheme === 'default'
          ? {
              textDecoration: 'none',
              backgroundColor: isStandard ? undefined : 'tints.darkCream',
              border: '1px solid',
              borderColor: 'tints.white',
              boxShadow: 'lg',
            }
          : {}
      }
      cursor={linkTo ? 'pointer' : 'default'}
    >
      {/* First Box */}
      <Flex
        flexDirection="column"
        flex={[2, 1]}
        minHeight={firstBoxMinHeight}
        margin={firstMarginBox}
      >
        {tag ? (
          <Box position="absolute" zIndex={1} marginTop="2" marginLeft="2">
            {tag ? tag : null}
          </Box>
        ) : null}
        <Flex
          flex={isStandard ? 1 : 'unset'}
          justifyContent="center"
          margin={marginInnerBox1}
          position="relative"
          backgroundColor={
            isStandard
              ? colorSchemeMap[colorScheme].background
              : 'tints.lightCream'
          }
          borderRadius="xl"
          height={[isMd ? '100%' : '210px', '230px', '240px']}
        >
          <Flex
            position="relative"
            maxHeight={[350, 380]}
            minWidth={[isStandard && isMobile ? 126 : 'unset', null, null]}
            paddingX={imagePaddingX}
            paddingTop={imagePaddingTop}
            paddingBottom={isStandard ? [0, 6] : []}
            alignItems={['center', 'baseline']}
            overflow="hidden"
            backgroundColor={isStandard ? cardBackgroundColor : undefined}
            css={
              isStandard && {
                img: {
                  height: full,
                  objectFit: 'cover',
                  width: 'auto',
                },
              }
            }
          >
            {image}
          </Flex>
        </Flex>
      </Flex>

      {/* Second Box */}
      <Flex
        flex={['5', '1']}
        flexDirection="column"
        justifyContent="space-between"
      >
        {/* Content passed inside the component, Generally the product name and a short description */}
        <Box
          paddingTop={childrenTopPadding}
          color={colorSchemeMap[colorScheme].text}
          zIndex={1}
          margin={[null, 'auto']}
          paddingX={[null, 2]}
        >
          {children && children}
        </Box>
        {/* This holds pricing and cta */}
        <Flex
          justifyContent={centralizeCTA ? 'center' : 'space-between'}
          alignItems="center"
          padding={[isMd ? null : 2, 2]}
        >
          <Flex
            alignItems="center"
            color={colorSchemeMap[colorScheme].text}
            opacity={isStandard ? 0.6 : 1}
          >
            {pricingOverride ? pricingOverride : pricing}
          </Flex>
          <Box onClick={handleCTAClick}>{cta}</Box>
        </Flex>
      </Flex>
    </Link>
  );
}

export default ProductCardV2;
